import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';
import {
  FLAGS,
  LEVELS,
  MODULES,
  type Flag,
  type Level,
  type Module,
} from '../../permissions/modules.ts';

export const MemberKindSchema = z.enum(['internal', 'sub', 'client']);
export const MemberStatusSchema = z.enum(['invited', 'active', 'inactive']);

/**
 * A user's relationship to one company.
 *
 * Document id is the user's uid, so a rule can fetch exactly one document to
 * answer "is this person a member here" without a query. Permissions live on
 * the referenced role rather than inline, so changing a role updates everyone
 * holding it at once.
 */
export const MemberSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  kind: MemberKindSchema,
  roleId: IdSchema,
  status: MemberStatusSchema,
  /** `all` sees every job in the company; `assigned` sees only jobIds. */
  jobAccess: z.enum(['all', 'assigned']),
  jobIds: z.array(IdSchema).default([]),
  displayName: z.string().optional(),
  email: z.string().email().optional(),
  title: z.string().optional(),
  phone: z.string().optional(),
  /** Set when this membership came from a contact record (sub or client). */
  contactId: IdSchema.optional(),
});
export type Member = z.infer<typeof MemberSchema>;

// Zod's enum() wants a mutable tuple while the source lists are `as const`.
// The cast is only about that shape: the resulting schema still produces the
// literal Level and Flag unions, which is what lets Principal be strongly
// typed all the way from a Firestore document to a permission check.
const LevelEnum = z.enum(LEVELS as unknown as [Level, ...Level[]]);
const FlagEnum = z.enum(FLAGS as unknown as [Flag, ...Flag[]]);

/** One explicit level per module — a missing key is a validation error, not a default. */
export const ModuleLevelsSchema = z.object(
  Object.fromEntries(MODULES.map((m) => [m, LevelEnum])) as Record<Module, typeof LevelEnum>,
);

export const RoleSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().min(1),
  /** Built-in templates cannot be deleted, only cloned and edited. */
  builtIn: z.boolean().default(false),
  modules: ModuleLevelsSchema,
  flags: z.array(FlagEnum).default([]),
});
export type Role = z.infer<typeof RoleSchema>;

export const InviteSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  email: z.string().email(),
  kind: MemberKindSchema,
  roleId: IdSchema,
  /**
   * The directory record this invite was raised for (SCRUM-315).
   *
   * A sub or a client already exists as a contact before they have an
   * account — it is what their bid, their price and their bill hang off
   * — and carrying it here is what lets acceptance link the membership
   * to it without guessing from an email address. Optional, because an
   * address can be typed for somebody who is not in the directory at
   * all.
   */
  contactId: IdSchema.optional(),
  jobIds: z.array(IdSchema).default([]),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().optional(),
  revokedAt: z.string().datetime().optional(),
});
export type Invite = z.infer<typeof InviteSchema>;
